/**
 * AUTO-GENERATED — do not edit. Source: core/exceptions.py.
 * Regenerate: python scripts/generate_error_catalog.py
 *
 * HTTP_STATUS_CATALOG and APP_ERROR_CATALOG only. Behaviour that consumes
 * them (translateStatus, translateCode, format, from*) lives in the sibling
 * httpErrors.ts, hand-written, imported from here — never generated.
 */

export type StatusEntry = { name: string; description: string }

export const HTTP_STATUS_CATALOG: Record<number, StatusEntry> = {
  400: { name: 'Bad Request', description: 'the request body or parameters are invalid' },
  401: { name: 'Unauthorized', description: 'authentication is required — please log in' },
  403: { name: 'Forbidden', description: 'you do not have permission to perform this action' },
  404: { name: 'Not Found', description: 'the requested resource does not exist' },
  405: { name: 'Method Not Allowed', description: 'this endpoint does not accept this HTTP method' },
  409: { name: 'Conflict', description: 'the request conflicts with the current state of the resource' },
  413: { name: 'Payload Too Large', description: 'the uploaded file exceeds the maximum allowed size' },
  415: { name: 'Unsupported Media Type', description: 'the uploaded file type is not accepted' },
  422: { name: 'Unprocessable Entity', description: 'the request was understood but the data failed validation' },
  429: { name: 'Too Many Requests', description: 'rate limit or spend cap exceeded — please wait before retrying' },
  500: { name: 'Internal Server Error', description: 'an unexpected error occurred on the server' },
  502: { name: 'Bad Gateway', description: 'an upstream service returned an invalid response' },
  503: { name: 'Service Unavailable', description: 'the service is temporarily unavailable' },
  504: { name: 'Gateway Timeout', description: 'an upstream service did not respond in time' },
}

export const APP_ERROR_CATALOG: Record<string, string> = {
  AUTHENTICATION_REQUIRED: 'authentication is required — please log in to continue',
  TOKEN_EXPIRED: 'your session has expired — please sign in again',
  TOKEN_INVALID: 'the authentication token is not valid',
  REFRESH_TOKEN_EXPIRED: 'your session refresh token has expired — please sign in again',
  INVITATION_EXPIRED: 'this invitation link has expired',
  CIAM_ERROR: 'the authentication service is temporarily unavailable',
  INSUFFICIENT_ROLE: 'your account role does not have permission for this action',
  PRODUCT_NOT_ENTITLED: 'your account is not entitled to this product',
  FIRM_ISOLATION_VIOLATION: 'you attempted to access data belonging to another firm',
  ACCESS_PENDING: 'your access request is awaiting approval',
  PLATFORM_TERMS_REQUIRED: 'the platform Terms of Service must be accepted before continuing',
  PRODUCT_TERMS_REQUIRED: 'this product\'s terms addendum must be accepted before continuing',
  ENGAGEMENT_NOT_FOUND: 'the requested engagement does not exist',
  DEAL_NOT_FOUND: 'the requested deal does not exist',
  USER_NOT_FOUND: 'the specified user account does not exist',
  DOCUMENT_NOT_FOUND: 'the requested document does not exist',
  FILING_NOT_FOUND: 'the requested filing does not exist',
  STORAGE_FILE_NOT_FOUND: 'the file could not be found in storage',
  FILING_SLOT_LOCKED: 'a prior filing slot has not been completed — filings must be sequential',
  FILING_ALREADY_SUBMITTED: 'this filing has already been submitted and cannot be modified',
  ENGAGEMENT_LOCKED: 'this engagement is locked and cannot be edited',
  DEAL_LOCKED: 'this deal is locked and cannot be modified',
  FINANCIAL_STATEMENT_NOT_APPROVED: 'CPA approval of the financial statement is required before generating this document',
  NORMALIZATION_IN_PROGRESS: 'a normalization session is already running for this deal',
  RATE_LIMIT_EXCEEDED: 'too many requests — please wait before retrying',
  AI_SPEND_CAP_EXCEEDED: 'the AI processing budget for this period has been reached',
  DATABASE_UNAVAILABLE: 'the primary database is temporarily unreachable',
  DATABASE_QUERY_ERROR: 'a database query failed — contact support if this persists',
  DATABASE_TRANSACTION_FAILED: 'a database transaction could not be committed',
  NETWORK_ERROR: 'the request could not reach the server — check connectivity',
  REQUEST_TIMEOUT: 'the request timed out — the server took too long to respond',
  UPSTREAM_TIMEOUT: 'a required external service did not respond in time',
  UPSTREAM_ERROR: 'an external service returned an error',
  CONNECTION_REFUSED: 'the server refused the connection',
  STORAGE_READ_ERROR: 'failed to read the file from storage',
  STORAGE_WRITE_ERROR: 'failed to save the file to storage',
  UPLOAD_TOO_LARGE: 'the uploaded file exceeds the maximum allowed size',
  UNSUPPORTED_FILE_TYPE: 'the uploaded file type is not accepted',
  UNSUPPORTED_BANK_FORMAT: 'no parser matched the uploaded bank statement format',
  PARSE_ERROR: 'the uploaded document could not be read or processed',
  PARSE_VALIDATION_ERROR: 'the parsed data failed schema validation checks',
  BANK_STATEMENT_LIMIT_EXCEEDED: 'the maximum number of bank statements for this engagement has been reached',
  AI_TIMEOUT: 'the AI service did not respond in time — please try again',
  AI_CONTENT_POLICY: 'the AI service declined this content',
  AI_RESPONSE_MALFORMED: 'the AI returned a response that could not be processed',
  AI_CONTEXT_OVERFLOW: 'the document is too large to process in one AI call',
  AI_DISABLED: 'AI features have been switched off by your administrator',
  COMPLIANCE_FAILED: 'the filing did not pass compliance validation',
  REPORT_GENERATION_FAILED: 'the report could not be generated — check the deal data',
  VALUATION_NOT_FOUND: 'the requested valuation engagement does not exist',
  VALUATION_ROLE_REQUIRED: 'valuation access requires a valuation role on your account',
  CONCLUSION_REQUIRES_VALUATION_CREDENTIAL: 'a conclusion of value requires an attested valuation credential (ABV, ASA, CVA or CBA) — this role may run a calculation engagement',
  VALUATION_INPUT_INVALID: 'the valuation inputs cannot be run by the calculation engine',
  VALUATION_OVERRIDE_ROLE_REQUIRED: 'overriding a calculated valuation input requires a valuation role on this engagement',
  VALUATION_PROFILE_NOT_SIGNED: 'no signed industry profile is available to value against — a valuation cannot run on unsigned configuration',
  VALUATION_NOT_FINALIZED: 'the valuation record is not a signed, current snapshot — finalize it before producing a document',
  VALUATION_DISCLAIMER_REQUIRED: 'Accept the estimate disclaimer to continue to payment and see your value.',
  REISSUE_NO_CHANGE: 'nothing that can move the number has changed since the live version — re-finalizing would write an identical record',
  FINANCIALS_UNCONFIRMED: 'please confirm your financials first',
  SET_ASIDE_REASON_REQUIRED: 'Before you sign, give a reason for each approach you set aside, on the Methodology and rates step.',
  ATTEST_REFUSED: 'the requested letter cannot be issued as asked',
  ATTEST_POLICY_INVALID: 'the assertion policy could not be loaded — letters are unavailable until it is fixed',
  ATTEST_ASSERTION_DENIED: 'the letter contains an assertion a CPA may not make',
  ATTEST_ASSURANCE_EXCEEDED: 'the wording conveys more assurance than this letter type supports',
  ATTEST_ENGAGEMENT_REFUSED: 'no permissible letter type covers this request',
  ATTEST_INDEPENDENCE_IMPAIRED: 'the proposed issuing firm is not independent of the subject entity',
  ATTEST_ISSUER_NOT_ELIGIBLE: 'the proposed issuing firm is not eligible to perform this engagement',
  ATTEST_EVIDENCE_MISSING: 'a factual statement has no source document or procedure linked to it',
  ATTEST_EVIDENCE_NOT_VERIFIED: 'the linked source document does not support the statement it is cited for',
  ATTEST_LETTER_TYPE_UNKNOWN: 'that letter type is not in the registry for this deployment',
  ATTEST_INPUT_MISSING: 'a fact this letter type requires was not supplied',
  ATTEST_FIRM_PROFILE_MISSING: 'no active firm profile — a letter cannot be signed under credentials nobody activated',
  VALIDATION_ERROR: 'the submitted data did not pass validation',
  INTERNAL_ERROR: 'an unexpected server error occurred',
}
